/**
 * `B-01` — l'écran de connexion du back-office, en **deux temps**.
 *
 * **Composé, non maquetté.** Les maquettes back-office portent les seize écrans
 * de module ; la connexion n'en fait pas partie. L'écran est donc composé
 * depuis le système de design : les jetons engendrés pour toute valeur, et les
 * classes de formulaire déjà livrées (`.form__field`, `.form__label`,
 * `.form__input`, `.form__submit`) pour les champs et le bouton, de sorte qu'un
 * changement du formulaire du back-office emmène la connexion avec lui.
 *
 * **Deux temps, et c'est le second facteur qui les sépare** (`EX-BO-001`).
 * Jusqu'au chantier B8, le second facteur était un secret statique : deux
 * choses qu'on **sait** ne font pas deux facteurs. C'est désormais un code
 * envoyé par SMS — la possession du téléphone. L'écran demande donc
 * l'identifiant et le mot de passe, puis, et seulement si le serveur les
 * accepte, le code reçu.
 *
 * **Ce que l'écran ne fait pas, et qui compte plus que ce qu'il fait :** il ne
 * décide jamais si un envoi doit partir. Le serveur seul le décide, et il ne le
 * fait que sur un identifiant et un mot de passe justes. Un écran qui
 * demanderait le code avant d'avoir été autorisé à le faire n'aurait rien
 * cassé — la route l'aurait refusé —, mais il aurait laissé croire que la
 * garde est ici.
 *
 * **Un seul message de refus.** Identifiants faux, mot de passe faux, refus
 * en amont, code faux, code expiré, code rejoué, billet inconnu : le même
 * texte, au caractère près. Ce n'est pas une politesse, c'est `RG-06` — un
 * message qui distinguerait les causes dirait à un attaquant lequel des
 * obstacles il a franchi.
 *
 * **Une passerelle en panne se dit.** Le serveur rend `DEGRADED` quand l'envoi
 * n'a pas pu être **demandé** à la passerelle. L'écran reste alors au premier
 * temps et l'annonce : il ne présente jamais un champ de code pour un code qui
 * n'est jamais parti. Ce qui est affirmé s'arrête là — la réponse immédiate de
 * l'opérateur ne distingue pas un envoi accepté d'un refus d'authentification
 * (`D-18`), donc « demandé à la passerelle » est tout ce qu'on peut dire, et
 * l'écran ne dit rien de plus.
 *
 * **Le double envoi est impossible.** Un verrou en mémoire, pris avant l'appel
 * et rendu après, s'ajoute à la désactivation du bouton : la désactivation
 * passe par un rendu, et deux clics assez rapprochés partent avant ce rendu.
 * C'est la même garde que le formulaire générique pose, portée ici jusqu'au
 * chemin d'appel, et elle vaut pour les deux temps.
 */

import { useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import { homeModulePath } from './routes.js';
import { intendedDestinationOf } from './session-guard.js';
import { ApiError } from '../http/api-client.js';
import { useApiClient } from '../http/api-context.js';
import {
  completeBackOfficeSession,
  openBackOfficeChallenge,
} from '../http/back-office-session.js';
import type { BackOfficeChallenge } from '../http/back-office-session.js';
import { useSession, useSessionStore } from '../state/session-context.js';

/** Ce que le premier temps recueille. */
interface CredentialsFormValues {
  username: string;
  password: string;
}

/** Ce que le second temps recueille. */
interface CodeFormValues {
  code: string;
}

/** La description d'un champ : rien d'affichable, seulement des clés. */
interface LoginField {
  readonly name: string;
  readonly labelKey: string;
  readonly inputType: string;
  readonly autoComplete: string;
  readonly inputMode?: 'numeric';
  readonly hintKey?: string;
}

/** Les deux champs du premier temps. */
const credentialFields: readonly LoginField[] = [
  {
    name: 'username',
    labelKey: 'login.username',
    inputType: 'text',
    autoComplete: 'username',
  },
  {
    name: 'password',
    labelKey: 'login.password',
    inputType: 'password',
    autoComplete: 'current-password',
  },
];

/** L'unique champ du second temps. */
const codeField: LoginField = {
  name: 'code',
  labelKey: 'login.code',
  inputType: 'text',
  autoComplete: 'one-time-code',
  inputMode: 'numeric',
  hintKey: 'login.codeHint',
};

/** Le nombre de temps du parcours, écrit une fois. */
const totalSteps = 2;

/** Ce que l'écran a à dire après une tentative. */
interface LoginFailure {
  /** La clé du message affiché. */
  readonly messageKey: string;
  /**
   * L'identifiant de corrélation à citer dans un signalement, ou `null`.
   *
   * `null` sur un refus, et c'est délibéré : le refus doit être **identique**
   * pour toutes ses causes, et un identifiant qui change à chaque tentative
   * suffirait à distinguer deux rendus. Il n'a de sens que sur une panne, où
   * il sert à retrouver l'appel dans les journaux.
   */
  readonly reference: string | null;
}

/** L'écran de connexion. */
export function LoginScreen(): ReactNode {
  const { t } = useTranslation();
  const location = useLocation();
  const operator = useSession((etat) => etat.operator);
  const endReason = useSession((etat) => etat.endReason);
  const store = useSessionStore();
  const client = useApiClient();
  const identifiant = useId();
  const [echec, setEchec] = useState<LoginFailure | null>(null);
  const [passerelleMuette, setPasserelleMuette] = useState(false);
  // Le billet du premier temps. **Mémoire seulement**, comme le jeton de
  // session : il ouvre le second temps, c'est un secret au même titre.
  const [billet, setBillet] = useState<BackOfficeChallenge | null>(null);
  // Le verrou du double envoi. Une référence et non un état : elle est lue et
  // écrite dans le même tour, là où un état ne changerait qu'au rendu suivant.
  const enVol = useRef(false);

  const identifiants = useForm<CredentialsFormValues>({ mode: 'onSubmit' });
  const codeSaisi = useForm<CodeFormValues>({ mode: 'onSubmit' });

  const destination = intendedDestinationOf(location) ?? homeModulePath;

  const demanderLeCode = async (valeurs: CredentialsFormValues): Promise<void> => {
    if (enVol.current) return;
    enVol.current = true;
    setEchec(null);
    setPasserelleMuette(false);
    try {
      const emis = await openBackOfficeChallenge(client, {
        username: valeurs.username,
        password: valeurs.password,
      });
      if (emis.delivery === 'DELIVERED') {
        setBillet(emis);
      } else {
        // Aucun code n'est en route : rester au premier temps. Présenter un
        // champ de code ici ferait attendre un message qui ne viendra pas.
        setPasserelleMuette(true);
      }
    } catch (erreur) {
      setEchec(decrireEchec(erreur));
    } finally {
      enVol.current = false;
    }
  };

  const ouvrirLaSession = async (valeurs: CodeFormValues): Promise<void> => {
    if (enVol.current || billet === null) return;
    enVol.current = true;
    setEchec(null);
    try {
      const session = await completeBackOfficeSession(client, {
        ticket: billet.ticket,
        code: valeurs.code,
      });
      store.getState().openSession(session);
    } catch (erreur) {
      setEchec(decrireEchec(erreur));
    } finally {
      enVol.current = false;
    }
  };

  const recommencer = (): void => {
    setBillet(null);
    setEchec(null);
    setPasserelleMuette(false);
    codeSaisi.reset();
  };

  // Une session déjà ouverte mène à la coquille — et à la destination visée si
  // la garde en a retenu une. Redemander un mot de passe à qui en a déjà donné
  // un serait une seconde connexion sans raison.
  if (operator !== null) return <Navigate replace to={destination} />;

  const etape = billet === null ? 1 : 2;

  return (
    <main className="login" aria-labelledby={`${identifiant}-heading`}>
      <div className="login__stage">
        <div className="login__brand">
          <span className="login__mark" aria-hidden="true" />
          <span className="login__identity">
            <span className="login__name">{t('app.name')}</span>
            <span className="login__environment">{t('app.environment')}</span>
          </span>
        </div>

        <section className="login__panel">
          <p className="login__step">{t('login.step', { current: etape, total: totalSteps })}</p>

          <h1 className="login__heading" id={`${identifiant}-heading`}>
            {billet === null ? t('login.heading') : t('login.codeHeading')}
          </h1>
          <p className="login__intro">
            {billet === null
              ? t('login.intro')
              : t('login.codeIntro', {
                  length: billet.codeLength,
                  minutes: Math.round(billet.expiresInSeconds / 60),
                  attempts: billet.maxAttempts,
                })}
          </p>

          {endReason === 'expired' ? (
            <p className="login__notice" role="status">
              {t('login.expiredNotice')}
            </p>
          ) : null}

          {passerelleMuette ? (
            <p className="login__notice login__notice--gateway" role="alert">
              {t('login.gatewayDown')}
            </p>
          ) : null}

          {billet === null ? (
            <form
              className="form"
              noValidate
              onSubmit={(evenement) => {
                void identifiants.handleSubmit(demanderLeCode)(evenement);
              }}
            >
              {credentialFields.map((champ) =>
                renderField(champ, identifiant, t, identifiants),
              )}
              <button
                className="form__submit"
                type="submit"
                disabled={identifiants.formState.isSubmitting}
              >
                {identifiants.formState.isSubmitting
                  ? t('login.submitting')
                  : t('login.submit')}
              </button>
            </form>
          ) : (
            <form
              className="form"
              noValidate
              onSubmit={(evenement) => {
                void codeSaisi.handleSubmit(ouvrirLaSession)(evenement);
              }}
            >
              {renderField(codeField, identifiant, t, codeSaisi)}
              <button
                className="form__submit"
                type="submit"
                disabled={codeSaisi.formState.isSubmitting}
              >
                {codeSaisi.formState.isSubmitting
                  ? t('login.codeSubmitting')
                  : t('login.codeSubmit')}
              </button>
              <button className="login__restart" type="button" onClick={recommencer}>
                {t('login.restart')}
              </button>
            </form>
          )}

          {echec === null ? null : (
            <div className="login__feedback">
              <p className="login__error" role="alert">
                {t(echec.messageKey)}
              </p>
              {echec.reference === null ? null : (
                <p className="login__hint">
                  {t('login.correlation', { reference: echec.reference })}
                </p>
              )}
            </div>
          )}

          <p className="login__footer">{t('login.footer')}</p>
        </section>
      </div>
    </main>
  );
}

/**
 * Un champ, ses libellés, son aide et son erreur.
 *
 * Écrit une fois pour les deux temps : deux rendus séparés auraient divergé au
 * premier ajout d'attribut d'accessibilité, et c'est celui qu'on n'aurait pas
 * recopié qui aurait manqué.
 */
function renderField(
  champ: LoginField,
  identifiant: string,
  t: (cle: string, options?: Record<string, unknown>) => string,
  formulaire: {
    register: (nom: never, options: { required: string }) => Record<string, unknown>;
    formState: { errors: Record<string, { message?: unknown } | undefined> };
  },
): ReactNode {
  const idChamp = `${identifiant}-${champ.name}`;
  const idErreur = `${idChamp}-error`;
  const idAide = `${idChamp}-hint`;
  const erreur = formulaire.formState.errors[champ.name];
  const decrit = [
    erreur === undefined ? null : idErreur,
    champ.hintKey === undefined ? null : idAide,
  ].filter((valeur): valeur is string => valeur !== null);
  return (
    <div className="form__field" key={champ.name}>
      <label className="form__label" htmlFor={idChamp}>
        {t(champ.labelKey)}
      </label>
      <input
        className="form__input"
        id={idChamp}
        type={champ.inputType}
        inputMode={champ.inputMode}
        autoComplete={champ.autoComplete}
        aria-invalid={erreur === undefined ? undefined : true}
        aria-describedby={decrit.length === 0 ? undefined : decrit.join(' ')}
        {...formulaire.register(champ.name as never, {
          required: t('login.requiredField', { field: t(champ.labelKey) }),
        })}
      />
      {champ.hintKey === undefined ? null : (
        <p className="login__hint" id={idAide}>
          {t(champ.hintKey)}
        </p>
      )}
      {erreur === undefined ? null : (
        <p className="form__error" id={idErreur} role="alert">
          {String(erreur.message)}
        </p>
      )}
    </div>
  );
}

/**
 * Ce qu'il faut afficher d'une erreur d'appel.
 *
 * **Toutes les causes de refus rendent ici le même texte, et c'est la propriété
 * qui compte** : identifiant inconnu, mot de passe faux, second facteur faux,
 * refus en amont. Distinguer apprendrait à un attaquant laquelle de ses
 * hypothèses est juste — c'est-à-dire lui dire, à chaque essai, s'il approche.
 *
 * **`unauthorized` et `forbidden` sont ici volontairement confondus.** Le
 * transport les distingue depuis qu'un droit manquant ne doit plus être lu
 * comme une session finie (voir `ApiErrorKind`) — cette distinction sert
 * *après* la connexion, quand un opérateur identifié a le droit de savoir quel
 * droit lui manque. **Avant** la connexion, il n'y a pas d'opérateur : le `401`
 * de l'authentification et un `403` rendu en amont — par le mandataire, par
 * exemple — sont deux refus de la même porte, et les séparer dirait à qui
 * essaie que son mot de passe, lui, était bon.
 *
 * Une erreur qui n'est pas une `ApiError` est traitée comme une panne serveur :
 * afficher son message reviendrait à montrer un texte technique à un analyste,
 * et à risquer d'y laisser passer ce que la requête transportait.
 */
function decrireEchec(erreur: unknown): LoginFailure {
  if (!(erreur instanceof ApiError)) {
    return { messageKey: 'errors.server', reference: null };
  }
  if (erreur.kind === 'unauthorized' || erreur.kind === 'forbidden') {
    return { messageKey: 'login.refused', reference: null };
  }
  return { messageKey: erreur.messageKey, reference: erreur.correlationId };
}
